import Icon, { IconType } from 'components/Icon';
import { Quality } from 'dragonmouse-shared/interfaces';
import { CurrencyLevel } from 'economy';
import useToast from 'hooks/useToast';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectWalletLevels } from 'redux/db/reducers';
import { setGoldTooltipState, setSilverTooltipState, setChatQuality } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import * as S from './styles';
import { selectIsMobile } from 'redux/mode/reducers';
import { Tooltip } from 'components/Widget/Tooltip';

const QualityToggle = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { message: toastMessage, isVisible: isToastVisible, showToast } = useToast();

	const { walletLevels, showGoldTooltip, showSilverTooltip, isMobile, chatQuality } = useSelector(
		(r: RootReducer) => {
			return {
				walletLevels: selectWalletLevels(r.db),
				showGoldTooltip: r.mode.goldTooltipState === 'visible',
				showSilverTooltip: r.mode.silverTooltipState === 'visible',
				isMobile: selectIsMobile(r.mode),
				chatQuality: r.mode.chatQuality,
			};
		},
		shallowEqual
	);

	const onDismissGoldTooltip = () => {
		dispatch(setGoldTooltipState('dismissed'));
	};

	const onDismissSilverTooltip = () => {
		dispatch(setSilverTooltipState('dismissed'));
	};
	const tooltipId = 'quality-toggle-tooltip';

	const renderQualityCircle = (
		qualityLevel: Quality,
		mobileToastMessage: string,
		walletLevel: CurrencyLevel | undefined
	) => {
		const isActive = chatQuality === qualityLevel;

		let tooltipContent =
			qualityLevel === Quality.SILVER
				? 'Silver flows easily — use it to shape and explore freely.'
				: 'Gold is for high precision — save it for the moments that matter most.';

		const currencyName = qualityLevel === Quality.SILVER ? 'silver' : 'gold';
		if (walletLevel === 'LOW') tooltipContent += `<br><br>⚠️ ${currencyName} is low.`;
		if (walletLevel === 'EMPTY') tooltipContent += `<br><br>⚠️ You are out of ${currencyName}.`;

		// Wrap content in div with inline styles to ensure proper wrapping
		tooltipContent = `<div style="max-width: 240px; line-height: 1.6; word-wrap: break-word; white-space: normal; overflow-wrap: break-word;">${tooltipContent}</div>`;

		return (
			<S.QualityCircleWrapper
				data-tooltip-id={tooltipId}
				data-tooltip-html={tooltipContent}
				walletLevel={walletLevel}
				isActive={isActive}
				onClick={() => {
					dispatch(setChatQuality(qualityLevel));
					if (isMobile) {
						showToast(mobileToastMessage);
					}
				}}
			>
				<S.QualityCircle quality={qualityLevel} isActive={isActive} walletLevel={walletLevel}>
					{walletLevel === 'LOW' && (
						<S.ErrorIconOverlay>
							<Icon type={IconType.BANG} width={15} height={15} color={theme.gray[100]} />
						</S.ErrorIconOverlay>
					)}
				</S.QualityCircle>
				<Tooltip id={tooltipId} placement="top" />
			</S.QualityCircleWrapper>
		);
	};

	return (
		<>
			{isMobile && toastMessage && (
				<S.MobileToast isVisible={isToastVisible} role="status" aria-live="polite">
					{toastMessage}
				</S.MobileToast>
			)}
			<S.QualityToggleWrapper>
				{showGoldTooltip && (
					<S.TooltipMessage>
						<S.TooltipMessageDescription>
							Need top-quality results? Switch to Gold for your most important iterations.
						</S.TooltipMessageDescription>
						<S.TooltipMessageDismissButton type="button" onClick={onDismissGoldTooltip}>
							Dismiss
						</S.TooltipMessageDismissButton>
						<S.TooltipMessageCaret />
					</S.TooltipMessage>
				)}
				{showSilverTooltip && (
					<S.TooltipMessage>
						<S.TooltipMessageDescription>
							Want faster responses? Switch to Silver for quicker iterations.
						</S.TooltipMessageDescription>
						<S.TooltipMessageDismissButton type="button" onClick={onDismissSilverTooltip}>
							Dismiss
						</S.TooltipMessageDismissButton>
						<S.TooltipMessageCaret />
					</S.TooltipMessage>
				)}
				<S.QualityToggleContainer isMobile={isMobile}>
					{renderQualityCircle(
						Quality.SILVER,
						'Silver: explore quickly',
						walletLevels?.silverLevel
					)}
					{renderQualityCircle(Quality.GOLD, 'Gold: highest quality', walletLevels?.goldLevel)}
				</S.QualityToggleContainer>
			</S.QualityToggleWrapper>
		</>
	);
};

export default QualityToggle;
